import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  ShoppingCart as CartIcon,
  Plus,
  Minus,
  Trash2,
  Truck,
  Package,
  Users,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Filter,
  Search,
  Info,
  Clock,
  User,
  Tag,
  RefreshCw,
  Building2,
  ShoppingBag,
  X,
  Eye
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { SupplierMOQBanner } from '../components/procurement/SupplierMOQBanner';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShoppingCart = () => {
  // State
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState({});
  const [filterType, setFilterType] = useState('all'); // all, reposition, client_order
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add product dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addOrderType, setAddOrderType] = useState('reposition');
  const [addClientRef, setAddClientRef] = useState('');
  const [addNotes, setAddNotes] = useState('');
  
  // Create orders dialog
  const [showCreateOrdersDialog, setShowCreateOrdersDialog] = useState(false);
  const [creatingOrders, setCreatingOrders] = useState(false);
  const [createdOrders, setCreatedOrders] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState({}); // Track which suppliers are selected
  
  // Review mode state (Step 2 - Review order before creating)
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewSupplier, setReviewSupplier] = useState(null); // Current supplier being reviewed
  const [reviewItems, setReviewItems] = useState([]); // Items for current supplier
  const [reviewQuantities, setReviewQuantities] = useState({}); // Editable quantities
  const [pendingReviewSuppliers, setPendingReviewSuppliers] = useState([]); // Suppliers still to review
  const [sendToSage, setSendToSage] = useState(true); // Toggle for SAGE integration
  
  // Export options
  const [exportArmazem, setExportArmazem] = useState('001');
  const [exportFormato, setExportFormato] = useState('xlsx');

  // Load cart and products
  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/shopping-cart`);
      setCartItems(response.data.items || []);
      setCartSummary(response.data.suppliers_summary || {});
      setLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar carrinho');
      setLoading(false);
    }
  };

  // Server-side product search for the "Add to cart" modal (Sessão 26 — Q3 fix).
  // Previously this loaded 200 products and filtered client-side, which
  // silently dropped any SKU outside the first 200 of ~14.4k products.
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const query = productSearch.trim();
    // Don't fire requests for very short queries (server has 14k+ products)
    if (query.length < 2) {
      setAllProducts([]);
      setSearchTotal(0);
      return undefined;
    }
    setSearchingProducts(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${API}/products?search=${encodeURIComponent(query)}&page_size=100`
        );
        if (!cancelled) {
          setAllProducts(response.data.items || []);
          setSearchTotal(response.data.total || 0);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Erro ao pesquisar produtos', error);
          setAllProducts([]);
          setSearchTotal(0);
        }
      } finally {
        if (!cancelled) setSearchingProducts(false);
      }
    }, 250);  // 250ms debounce
    return () => { cancelled = true; clearTimeout(timer); };
  }, [productSearch]);

  // Filter cart items
  const filteredItems = useMemo(() => {
    return cartItems.filter(item => {
      if (filterType !== 'all' && item.order_type !== filterType) return false;
      if (filterSupplier !== 'all' && item.supplier !== filterSupplier) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!item.sku.toLowerCase().includes(query) && 
            !item.product_name.toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [cartItems, filterType, filterSupplier, searchQuery]);

  // Get unique suppliers from cart
  const suppliers = useMemo(() => {
    const unique = [...new Set(cartItems.map(i => i.supplier))];
    return unique.sort();
  }, [cartItems]);

  // Products list for the "Add to cart" modal. The backend already filtered
  // by the search query (server-side), so we just take what we got.
  const filteredProducts = useMemo(() => allProducts, [allProducts]);

  // Handlers
  const handleAddToCart = async () => {
    if (!selectedProduct) return;
    
    try {
      const response = await axios.post(`${API}/shopping-cart`, {
        product_id: selectedProduct.id,
        quantity: addQuantity,
        order_type: addOrderType,
        client_reference: addClientRef || null,
        notes: addNotes || null
      });
      
      if (response.data.status === 'duplicate') {
        toast.warning(response.data.message, {
          description: `Quantidade atual: ${response.data.existing_item.quantity}`,
          action: {
            label: 'Atualizar',
            onClick: () => handleUpdateQuantity(response.data.existing_item.id, addQuantity)
          }
        });
      } else {
        toast.success(response.data.message);
        fetchCart();
      }
      
      // Reset form
      setSelectedProduct(null);
      setAddQuantity(1);
      setAddOrderType('reposition');
      setAddClientRef('');
      setAddNotes('');
      setShowAddDialog(false);
      
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    }
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    try {
      await axios.put(`${API}/shopping-cart/${itemId}`, { quantity: newQuantity });
      fetchCart();
      toast.success('Quantidade atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await axios.delete(`${API}/shopping-cart/${itemId}`);
      fetchCart();
      toast.success('Item removido');
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleCreateOrders = async () => {
    // Get list of selected suppliers
    const suppliersToCreate = Object.entries(selectedSuppliers)
      .filter(([_, isSelected]) => isSelected)
      .map(([supplier, _]) => supplier);
    
    if (suppliersToCreate.length === 0) {
      toast.error('Selecione pelo menos um fornecedor');
      return;
    }

    // Start review mode with first supplier
    setPendingReviewSuppliers(suppliersToCreate.slice(1)); // Remaining suppliers
    startReviewForSupplier(suppliersToCreate[0]);
    setShowCreateOrdersDialog(false);
  };

  // Start review mode for a specific supplier
  const startReviewForSupplier = (supplierName) => {
    const supplierItems = filteredItems.filter(item => 
      (item.supplier || 'Sem fornecedor') === supplierName
    );
    
    // Initialize quantities from current cart items
    const initialQuantities = {};
    supplierItems.forEach(item => {
      initialQuantities[item.id] = item.quantity;
    });
    
    setReviewSupplier(supplierName);
    setReviewItems(supplierItems);
    setReviewQuantities(initialQuantities);
    setReviewMode(true);
  };

  // Update quantity in review mode
  const handleReviewQuantityChange = (itemId, newQty) => {
    const qty = Math.max(0, parseInt(newQty) || 0);
    setReviewQuantities(prev => ({ ...prev, [itemId]: qty }));
  };

  // Remove line from review
  const handleRemoveReviewLine = (itemId) => {
    setReviewQuantities(prev => ({ ...prev, [itemId]: 0 }));
  };

  // Cancel review mode
  const handleCancelReview = () => {
    setReviewMode(false);
    setReviewSupplier(null);
    setReviewItems([]);
    setReviewQuantities({});
    setPendingReviewSuppliers([]);
  };

  // Confirm and create order for current supplier
  const handleConfirmReviewOrder = async () => {
    setCreatingOrders(true);
    
    try {
      // Get valid items (qty > 0)
      const validItems = reviewItems.filter(item => (reviewQuantities[item.id] || 0) > 0);
      
      if (validItems.length === 0) {
        toast.error('Adicione pelo menos um item à encomenda');
        setCreatingOrders(false);
        return;
      }

      // Update quantities in cart before creating order (with error handling)
      for (const item of validItems) {
        const newQty = reviewQuantities[item.id];
        if (newQty !== item.quantity) {
          try {
            await axios.put(`${API}/shopping-cart/${item.id}`, { quantity: newQty });
          } catch (err) {
            // If item not found, it may have been deleted - continue anyway
            if (err.response?.status !== 404) {
              console.error(`Error updating item ${item.id}:`, err);
            }
          }
        }
      }

      // Remove items with qty = 0 (with error handling)
      for (const item of reviewItems) {
        if ((reviewQuantities[item.id] || 0) === 0) {
          try {
            await axios.delete(`${API}/shopping-cart/${item.id}`);
          } catch (err) {
            // Item may already be deleted - ignore 404
            if (err.response?.status !== 404) {
              console.error(`Error deleting item ${item.id}:`, err);
            }
          }
        }
      }

      // Create order for this supplier
      const response = await axios.post(`${API}/shopping-cart/create-orders`, null, {
        params: {
          supplier: reviewSupplier,
          order_type: filterType !== 'all' ? filterType : undefined,
          send_to_sage: sendToSage
        }
      });
      
      // Check for SAGE integration failures
      const sageIntegrationFailed = response.data?.sage_integration_failed;
      const sageErrorMessage = response.data?.sage_error_message;
      const sageSummary = response.data?.sage_integration_summary;
      
      if (sageIntegrationFailed && sageErrorMessage) {
        // Show SAGE error prominently
        toast.error(`Encomenda criada mas NÃO integrada no SAGE: ${sageErrorMessage}`, {
          duration: 8000,
        });
      } else if (sageSummary?.failed > 0) {
        // Partial failure
        toast.warning(`Encomenda criada. ${sageSummary.successful} integrada(s), ${sageSummary.failed} falha(s) SAGE.`, {
          duration: 6000,
        });
      } else if (sendToSage && sageSummary?.successful > 0) {
        toast.success(`Encomenda criada e integrada no SAGE para ${reviewSupplier}!`);
      } else {
        toast.success(`Encomenda criada para ${reviewSupplier}!`);
      }
      
      // Check if there are more suppliers to review
      if (pendingReviewSuppliers.length > 0) {
        const nextSupplier = pendingReviewSuppliers[0];
        setPendingReviewSuppliers(prev => prev.slice(1));
        await fetchCart(); // Refresh cart
        startReviewForSupplier(nextSupplier);
      } else {
        // All done - show success
        setCreatedOrders({
          total_orders: 1,
          total_items: validItems.length,
          total_amount: validItems.reduce((sum, item) => sum + (reviewQuantities[item.id] * item.unit_price), 0),
          orders: response.data.orders || []
        });
        setReviewMode(false);
        fetchCart();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar encomenda');
    } finally {
      setCreatingOrders(false);
    }
  };

  const handleExportExcel = async (poNumber) => {
    try {
      const response = await axios.get(
        `${API}/purchase-orders/${poNumber}/export-excel`,
        { 
          responseType: 'blob',
          params: { armazem: exportArmazem, formato: exportFormato }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${poNumber}_SAGE100C.${exportFormato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Ficheiro exportado!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  // Calculate totals for display
  const totals = useMemo(() => {
    const total = filteredItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const repositionItems = filteredItems.filter(i => i.order_type === 'reposition');
    const clientItems = filteredItems.filter(i => i.order_type === 'client_order');
    
    return {
      total,
      repositionCount: repositionItems.length,
      clientCount: clientItems.length,
      repositionValue: repositionItems.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0),
      clientValue: clientItems.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0)
    };
  }, [filteredItems]);

  // Sessão 32 — QW5: enrich cartSummary with items + MOQ + freight thresholds.
  // The backend `suppliers_summary` doesn't carry items[]; we enrich client-side
  // so SupplierMOQBanner can flag per-line MOQ issues without an extra round-trip.
  const enrichedCartSummary = useMemo(() => {
    const bySupplier = {};
    filteredItems.forEach((item) => {
      const sup = item.supplier || 'Sem fornecedor';
      if (!bySupplier[sup]) {
        bySupplier[sup] = {
          count: 0,
          total: 0,
          items: [],
          moq_value: item.supplier_moq_value || 0,
          free_freight_threshold: item.supplier_free_freight_threshold || 0,
        };
      }
      bySupplier[sup].count += 1;
      bySupplier[sup].total += (item.quantity || 0) * (item.unit_price || 0);
      bySupplier[sup].items.push({
        sku: item.sku,
        quantity: item.quantity,
        moq: item.moq || 1,
        unit_price: item.unit_price,
      });
    });
    return bySupplier;
  }, [filteredItems]);

  // Initialize selected suppliers when dialog opens
  const openCreateOrdersDialog = () => {
    // Select all suppliers by default
    const allSelected = {};
    Object.keys(enrichedCartSummary).forEach(supplier => {
      allSelected[supplier] = true;
    });
    setSelectedSuppliers(allSelected);
    setShowCreateOrdersDialog(true);
  };

  // Toggle supplier selection
  const toggleSupplierSelection = (supplier) => {
    setSelectedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier]
    }));
  };

  // Select/deselect all suppliers
  const toggleAllSuppliers = (selectAll) => {
    const newSelected = {};
    Object.keys(enrichedCartSummary).forEach(supplier => {
      newSelected[supplier] = selectAll;
    });
    setSelectedSuppliers(newSelected);
  };

  // Count selected suppliers
  const selectedSuppliersCount = Object.values(selectedSuppliers).filter(Boolean).length;
  const allSuppliersSelected = selectedSuppliersCount === Object.keys(enrichedCartSummary).length;
  const someSuppliersSelected = selectedSuppliersCount > 0 && !allSuppliersSelected;

  // Calculate review totals
  const reviewValidItems = reviewItems.filter(item => (reviewQuantities[item.id] || 0) > 0);
  const reviewTotalUnits = reviewValidItems.reduce((sum, item) => sum + (reviewQuantities[item.id] || 0), 0);
  const reviewTotalValue = reviewValidItems.reduce((sum, item) => sum + (reviewQuantities[item.id] || 0) * item.unit_price, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Step 2: Review Order View
  if (reviewMode && reviewSupplier) {
    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="review-order-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Button
              variant="ghost"
              onClick={handleCancelReview}
              className="mb-2 -ml-2"
              disabled={creatingOrders}
              data-testid="review-cancel-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Sair sem criar
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Eye className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              Rever Encomenda
            </h1>
            <p className="text-base sm:text-lg font-medium text-red-600 mt-1">{reviewSupplier}</p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Ajusta quantidades ou remove linhas. A encomenda só é criada quando confirmares.
            </p>
          </div>
          {pendingReviewSuppliers.length > 0 && (
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              +{pendingReviewSuppliers.length} fornecedor(es) a seguir
            </Badge>
          )}
        </div>

        {/* Summary Banner */}
        <Card className="border-2 border-red-200 rounded-sm shadow-none mb-4 sm:mb-6 bg-red-50/30">
          <CardContent className="p-4 sm:pt-6">
            <div className="grid grid-cols-3 gap-4 sm:gap-6">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Artigos</p>
                <p className="text-xl sm:text-2xl font-bold font-mono" data-testid="review-total-lines">{reviewValidItems.length}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Unidades</p>
                <p className="text-xl sm:text-2xl font-bold font-mono">{reviewTotalUnits}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Valor Total</p>
                <p className="text-lg sm:text-2xl font-bold font-mono text-red-600 truncate" data-testid="review-total-value">€{reviewTotalValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed lines table */}
        <Card className="border border-slate-200 rounded-sm shadow-none mb-4 sm:mb-6">
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="sm:hidden divide-y divide-slate-100" data-testid="review-order-lines-mobile">
              {reviewItems.map((item, index) => {
                const qty = reviewQuantities[item.id] || 0;
                return (
                  <div key={item.id} className={`p-3 ${qty === 0 ? 'opacity-50 bg-slate-50' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">#{index + 1}</span>
                          <span className="font-mono text-xs font-semibold text-red-600">{item.sku}</span>
                        </div>
                        <p className="text-sm font-medium mt-1">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">Stock: {item.current_stock || 0} · €{item.unit_price.toFixed(2)}/un</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveReviewLine(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`review-remove-mobile-${item.sku}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <Input
                        type="number"
                        min="0"
                        value={qty}
                        onChange={(e) => handleReviewQuantityChange(item.id, e.target.value)}
                        className="w-20 h-8 text-center font-mono"
                        data-testid={`review-qty-mobile-${item.sku}`}
                      />
                      <span className="font-mono font-medium">€{(qty * item.unit_price).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-xs text-muted-foreground">
                  <tr>
                    <th className="py-3 px-4 text-left">Linha</th>
                    <th className="py-3 px-4 text-left">SKU</th>
                    <th className="py-3 px-4 text-left">Descrição</th>
                    <th className="py-3 px-4 text-right">Stock</th>
                    <th className="py-3 px-4 text-center">Quantidade</th>
                    <th className="py-3 px-4 text-center">Unidade</th>
                    <th className="py-3 px-4 text-right">Preço Unit.</th>
                    <th className="py-3 px-4 text-right">Total</th>
                    <th className="py-3 px-4 text-center">Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewItems.map((item, index) => {
                    const qty = reviewQuantities[item.id] || 0;
                    return (
                      <tr key={item.id} className={`border-b border-slate-100 ${qty === 0 ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`review-line-${item.sku}`}>
                        <td className="py-3 px-4 text-muted-foreground">{index + 1}</td>
                        <td className="py-3 px-4 font-mono text-xs font-semibold text-red-600">{item.sku}</td>
                        <td className="py-3 px-4">{item.product_name}</td>
                        <td className="py-3 px-4 text-right font-mono">{item.current_stock || 0}</td>
                        <td className="py-3 px-4 text-center">
                          <Input
                            type="number"
                            min="0"
                            value={qty}
                            onChange={(e) => handleReviewQuantityChange(item.id, e.target.value)}
                            className="w-20 h-8 text-center font-mono mx-auto"
                            data-testid={`review-qty-${item.sku}`}
                          />
                        </td>
                        <td className="py-3 px-4 text-center text-muted-foreground">UN</td>
                        <td className="py-3 px-4 text-right font-mono">€{item.unit_price.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-mono">€{(qty * item.unit_price).toFixed(2)}</td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveReviewLine(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`review-remove-${item.sku}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-50 font-semibold">
                    <td colSpan="4" className="py-3 px-4 text-right">TOTAL</td>
                    <td className="py-3 px-4 font-mono text-center">{reviewTotalUnits}</td>
                    <td></td>
                    <td></td>
                    <td className="py-3 px-4 font-mono text-right text-red-600">€{reviewTotalValue.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* SAGE Integration Toggle */}
        <Card className="border border-slate-200 rounded-sm shadow-none mb-4 sm:mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="send-to-sage"
                  checked={sendToSage}
                  onCheckedChange={setSendToSage}
                />
                <div>
                  <Label htmlFor="send-to-sage" className="font-medium cursor-pointer">Criar ENF no SAGE 100C</Label>
                  <p className="text-xs text-muted-foreground">A encomenda será criada automaticamente no SAGE</p>
                </div>
              </div>
              <Badge variant={sendToSage ? "default" : "secondary"} className={sendToSage ? "bg-emerald-600" : ""}>
                {sendToSage ? "SAGE Activado" : "Apenas Local"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleCancelReview}
            disabled={creatingOrders}
            data-testid="review-cancel-btn-bottom"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleConfirmReviewOrder}
            disabled={creatingOrders || reviewValidItems.length === 0}
            data-testid="review-confirm-btn"
          >
            {creatingOrders ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                A criar...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirmar Encomenda ({reviewValidItems.length} artigo{reviewValidItems.length !== 1 ? 's' : ''})
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Show created orders confirmation
  if (createdOrders) {
    return (
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="orders-created-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-emerald-600 flex items-center gap-2">
              <CheckCircle className="h-8 w-8" />
              {createdOrders.total_orders === 1 ? 'Encomenda Criada' : `${createdOrders.total_orders} Encomendas Criadas`}
            </h1>
            <p className="text-muted-foreground">Uma encomenda por fornecedor para compatibilidade com SAGE 100C</p>
          </div>
          <Button onClick={() => setCreatedOrders(null)}>
            <CartIcon className="h-4 w-4 mr-2" />
            Voltar ao Carrinho
          </Button>
        </div>

        {/* Summary */}
        <Card className="border-2 border-emerald-200 bg-emerald-50/30 mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Encomendas</p>
                <p className="text-2xl font-bold font-mono text-emerald-700">{createdOrders.total_orders}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Itens</p>
                <p className="text-2xl font-bold font-mono">{createdOrders.total_items}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fornecedores</p>
                <p className="text-2xl font-bold font-mono">{createdOrders.total_orders}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="text-2xl font-bold font-mono text-emerald-600">€{createdOrders.total_amount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders */}
        {createdOrders.orders.map((order) => (
          <Card key={order.po_number} className="mb-4">
            <CardHeader className="bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    {order.po_number}
                  </CardTitle>
                  <p className="text-lg font-medium text-red-600">{order.supplier}</p>
                </div>
                <div className="text-right">
                  <Badge variant={order.order_type === 'client_order' ? 'destructive' : 'secondary'}>
                    {order.order_type === 'client_order' ? 'Pedido Cliente' : 
                     order.order_type === 'mixed' ? 'Misto' : 'Reposição'}
                  </Badge>
                  <p className="text-xl font-bold font-mono text-emerald-600 mt-1">€{order.total_amount.toFixed(2)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/30">
                    <th className="text-left py-2 px-4">SKU</th>
                    <th className="text-left py-2 px-4">Produto</th>
                    <th className="text-right py-2 px-4">Qtd</th>
                    <th className="text-right py-2 px-4">Preço</th>
                    <th className="text-right py-2 px-4">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={item.sku || idx} className="border-b">
                      <td className="py-2 px-4 font-mono text-xs">{item.sku}</td>
                      <td className="py-2 px-4">{item.product_name}</td>
                      <td className="py-2 px-4 font-mono text-right">{item.quantity}</td>
                      <td className="py-2 px-4 font-mono text-right">€{item.unit_price.toFixed(2)}</td>
                      <td className="py-2 px-4 font-mono text-right font-semibold">€{item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 border-t flex justify-end">
                <Button variant="outline" size="sm" onClick={() => handleExportExcel(order.po_number)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Exportar SAGE
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-between mt-6">
          <Link to="/purchase-orders">
            <Button variant="outline">Ver Todas as Encomendas</Button>
          </Link>
          <Button onClick={() => setCreatedOrders(null)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Novo Pedido
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="shopping-cart-page">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <CartIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
            Carrinho
          </h1>
          <p className="text-sm text-muted-foreground">
            Partilhado pela equipa • {cartItems.length} itens
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={fetchCart} className="flex-1 sm:flex-none">
            <RefreshCw className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Atualizar</span>
          </Button>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-none" size="sm" data-testid="add-to-cart-btn">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Adicionar Produto</span>
                <span className="sm:hidden">Adicionar</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-[500px] sm:max-w-[680px] lg:max-w-[860px] bg-white max-h-[85vh] overflow-hidden flex flex-col p-0">
              <DialogHeader className="p-4 pb-2 border-b">
                <DialogTitle>Adicionar ao Carrinho</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Product Search - Mobile Optimized */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Produto *</Label>
                  
                  {/* Selected Product Display */}
                  {selectedProduct && (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-sm text-red-600 font-semibold">{selectedProduct.sku}</p>
                        <p className="text-sm truncate">{selectedProduct.name}</p>
                        <p className="text-xs text-muted-foreground">{selectedProduct.supplier}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedProduct(null)}
                        className="h-8 w-8 p-0 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Search Input */}
                  {!selectedProduct && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Pesquisar SKU ou nome..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          className="pl-10 h-12 text-base"
                          data-testid="product-search-input"
                          autoComplete="off"
                        />
                      </div>
                      
                      {productSearch.length >= 2 && (
                        <div className="max-h-[280px] sm:max-h-[380px] overflow-y-auto border rounded-lg bg-white shadow-sm">
                          {searchingProducts ? (
                            <div className="p-4 text-center text-sm text-muted-foreground" data-testid="product-search-loading">
                              A pesquisar…
                            </div>
                          ) : filteredProducts.length === 0 ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              Nenhum produto encontrado
                            </div>
                          ) : (
                            <div className="divide-y">
                              {filteredProducts.map((product) => (
                                <button
                                  key={product.id}
                                  onClick={() => {
                                    setSelectedProduct(product);
                                    setProductSearch('');
                                  }}
                                  className="w-full text-left p-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                  data-testid={`product-option-${product.sku}`}
                                >
                                  <div className="flex justify-between items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-mono text-sm text-red-600 font-semibold">{product.sku}</p>
                                      <p className="text-sm truncate">{product.name}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`text-xs font-mono px-2 py-1 rounded ${(product.current_stock || 0) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                        stock {product.current_stock ?? 0}
                                      </span>
                                      {product.supplier && (
                                        <span className="hidden sm:inline text-xs text-muted-foreground bg-slate-100 px-2 py-1 rounded max-w-[180px] truncate">
                                          {product.supplier}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              ))}
                              {searchTotal > filteredProducts.length && (
                                <div className="p-2 text-center text-xs text-muted-foreground bg-slate-50" data-testid="search-more-hint">
                                  A mostrar {filteredProducts.length} de {searchTotal} — refina a pesquisa para veres os restantes
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {productSearch.length > 0 && productSearch.length < 2 && (
                        <p className="text-xs text-muted-foreground">Digite pelo menos 2 caracteres...</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Quantity - Touch Friendly */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Quantidade</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 flex-shrink-0"
                      onClick={() => setAddQuantity(Math.max(1, addQuantity - 1))}
                    >
                      <Minus className="h-5 w-5" />
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      value={addQuantity}
                      onChange={(e) => setAddQuantity(parseInt(e.target.value) || 1)}
                      className="h-12 text-center text-lg font-mono font-semibold"
                      data-testid="add-quantity"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-12 w-12 flex-shrink-0"
                      onClick={() => setAddQuantity(addQuantity + 1)}
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Order Type - Mobile Friendly Cards */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tipo de Encomenda</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAddOrderType('reposition')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        addOrderType === 'reposition' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Package className={`h-5 w-5 mb-1 ${addOrderType === 'reposition' ? 'text-blue-600' : 'text-slate-400'}`} />
                      <p className={`text-sm font-medium ${addOrderType === 'reposition' ? 'text-blue-900' : ''}`}>Reposição</p>
                      <p className="text-xs text-muted-foreground">Stock normal</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddOrderType('client_order')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        addOrderType === 'client_order' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Users className={`h-5 w-5 mb-1 ${addOrderType === 'client_order' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-sm font-medium ${addOrderType === 'client_order' ? 'text-orange-900' : ''}`}>Cliente</p>
                      <p className="text-xs text-muted-foreground">Pedido especial</p>
                    </button>
                  </div>
                </div>

                {/* Client Reference (if client order) */}
                {addOrderType === 'client_order' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Referência do Cliente</Label>
                    <Input
                      placeholder="Ex: Cliente ABC - Pedido #123"
                      value={addClientRef}
                      onChange={(e) => setAddClientRef(e.target.value)}
                      className="h-12"
                      data-testid="client-reference"
                    />
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Notas (opcional)</Label>
                  <Textarea
                    placeholder="Observações..."
                    value={addNotes}
                    onChange={(e) => setAddNotes(e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
              
              {/* Fixed Footer Button */}
              <div className="p-4 border-t bg-slate-50">
                <Button 
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-base"
                  disabled={!selectedProduct}
                  onClick={handleAddToCart}
                  data-testid="confirm-add-to-cart"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Adicionar ao Carrinho
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Card className="bg-slate-50">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <CartIcon className="h-6 w-6 sm:h-8 sm:w-8 text-slate-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
                <p className="text-lg sm:text-2xl font-bold font-mono truncate">€{totals.total.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Reposição</p>
                <p className="text-base sm:text-xl font-bold font-mono">{totals.repositionCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Clientes</p>
                <p className="text-base sm:text-xl font-bold font-mono">{totals.clientCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-emerald-50">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Fornecedores</p>
                <p className="text-base sm:text-xl font-bold font-mono">{suppliers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters - Collapsible on Mobile */}
      <Card className="mb-6">
        <CardContent className="p-3 sm:pt-4">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 sm:items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  <SelectItem value="reposition">Reposição</SelectItem>
                  <SelectItem value="client_order">Pedido Cliente</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-supplier">
                  <SelectValue placeholder="Fornecedor" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todos os Fornecedores</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Pesquisar SKU ou nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:max-w-xs"
                data-testid="search-cart"
              />
            </div>
            
            <div className="ml-auto">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={filteredItems.length === 0}
                onClick={openCreateOrdersDialog}
                data-testid="create-orders-btn"
              >
                <Truck className="h-4 w-4 mr-2" />
                Criar Encomendas ({filteredItems.length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cart Items */}
      {filteredItems.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 sm:py-12 text-center">
            <CartIcon className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-muted-foreground mb-2">Carrinho Vazio</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Adicione produtos ao carrinho
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowAddDialog(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Produto
              </Button>
              <Link to="/supplier-orders" className="w-full sm:w-auto">
                <Button className="bg-red-600 hover:bg-red-700 w-full">
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Ver Sugestões
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Sessão 32 — QW5: MOQ + free-freight banner per supplier */}
          <SupplierMOQBanner supplierSummary={enrichedCartSummary} testid="cart-moq" />

          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {filteredItems.map((item) => (
              <Card key={item.id} className="border border-slate-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs font-semibold text-red-600">{item.sku}</p>
                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">{item.supplier}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                      <Badge 
                        variant={item.order_type === 'client_order' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {item.order_type === 'client_order' ? 'Cliente' : 'Reposição'}
                      </Badge>
                      {item.origin && item.origin !== 'manual' && item.origin !== 'client_order' && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`origin-${item.sku}`}>
                          {item.origin === 'procurement_ai' ? 'Procurement AI'
                            : item.origin === 'warehouse_need' ? 'Necessidade Armazém'
                            : item.origin === 'tech_replenishment' ? 'Reposição Técnica'
                            : item.origin === 'internal' ? 'Pedido Interno' : item.origin}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {item.client_reference && (
                    <p className="text-xs text-orange-600 flex items-center gap-1 mb-2">
                      <Tag className="h-3 w-3" />
                      {item.client_reference}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="font-mono font-semibold w-10 text-center">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-mono text-lg font-semibold text-emerald-600">
                        €{(item.quantity * item.unit_price).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">€{item.unit_price.toFixed(2)}/un</p>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Mobile Total */}
            <Card className="bg-slate-50 border-2 border-slate-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total ({filteredItems.length} itens)</span>
                  <span className="text-xl font-bold font-mono text-emerald-600">€{totals.total.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Desktop Table View */}
          <Card className="hidden sm:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50/50">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">SKU</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Produto</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Fornecedor</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tipo</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Quantidade</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Preço</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Adicionado por</th>
                      <th className="text-center py-3 px-4 font-medium text-muted-foreground">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-slate-50/30">
                        <td className="py-3 px-4 font-mono text-xs font-semibold text-red-600">{item.sku}</td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            {item.client_reference && (
                              <p className="text-xs text-orange-600 flex items-center gap-1">
                                <Tag className="h-3 w-3" />
                                {item.client_reference}
                              </p>
                            )}
                            {item.notes && (
                              <p className="text-xs text-muted-foreground">{item.notes}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">{item.supplier}</td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={item.order_type === 'client_order' ? 'destructive' : 'secondary'}
                            className="text-xs"
                          >
                            {item.order_type === 'client_order' ? 'Cliente' : 'Reposição'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="font-mono font-semibold w-12 text-center">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-right">€{item.unit_price.toFixed(2)}</td>
                        <td className="py-3 px-4 font-mono text-right font-semibold">
                          €{(item.quantity * item.unit_price).toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>{item.added_by_user_name?.split('@')[0]}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(item.added_at).toLocaleDateString('pt-PT')}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveItem(item.id)}
                            data-testid={`remove-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td colSpan={4} className="py-3 px-4 text-right">TOTAL ({filteredItems.length} itens)</td>
                      <td className="py-3 px-4 font-mono text-right">
                        {filteredItems.reduce((sum, i) => sum + i.quantity, 0)}
                      </td>
                      <td></td>
                      <td className="py-3 px-4 font-mono text-right text-emerald-600">
                        €{totals.total.toFixed(2)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create Orders Dialog */}
      <Dialog open={showCreateOrdersDialog} onOpenChange={setShowCreateOrdersDialog}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Criar Encomendas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Selecionar Fornecedores</p>
                  <p className="text-blue-700">
                    Escolha quais fornecedores pretende encomendar agora. 
                    Será criada uma encomenda por fornecedor selecionado.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Fornecedores:</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => toggleAllSuppliers(true)}
                    className="text-xs h-7"
                  >
                    Selecionar Todos
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => toggleAllSuppliers(false)}
                    className="text-xs h-7"
                  >
                    Limpar
                  </Button>
                </div>
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto border rounded-lg p-2">
                {Object.entries(enrichedCartSummary).map(([supplier, data]) => (
                  <div 
                    key={supplier} 
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      selectedSuppliers[supplier] ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => toggleSupplierSelection(supplier)}
                    data-testid={`supplier-checkbox-${supplier.replace(/\s+/g, '-')}`}
                  >
                    <Checkbox 
                      checked={selectedSuppliers[supplier] || false}
                      onCheckedChange={() => toggleSupplierSelection(supplier)}
                      className="pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{supplier}</span>
                    </div>
                    <div className="text-right text-sm shrink-0">
                      <span className="text-muted-foreground">{data.count} itens</span>
                      <span className="ml-2 font-mono font-medium">€{data.total.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {selectedSuppliersCount > 0 && (
                <div className="text-sm text-muted-foreground text-right">
                  {selectedSuppliersCount} de {Object.keys(enrichedCartSummary).length} fornecedor(es) selecionado(s)
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código Armazém (SAGE)</Label>
                <Input
                  value={exportArmazem}
                  onChange={(e) => setExportArmazem(e.target.value)}
                  placeholder="001"
                />
              </div>
              <div className="space-y-2">
                <Label>Formato Excel</Label>
                <Select value={exportFormato} onValueChange={setExportFormato}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="xlsx">Excel 2007+ (.xlsx)</SelectItem>
                    <SelectItem value="xls">Excel 97-2003 (.xls)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCreateOrders}
              disabled={creatingOrders || selectedSuppliersCount === 0}
              data-testid="confirm-create-orders"
            >
              {creatingOrders ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  A criar encomendas...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Criar {selectedSuppliersCount} Encomenda(s)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShoppingCart;
